import { type Environment, pplCbTestEnvs } from '@root/@types/types';
import { tenantConfig } from '@root/config/tenants';

const env: Environment = import.meta.env?.VITE_ENVIRONMENT ?? process?.env?.ENVIRONMENT;
const client: string = import.meta.env?.VITE_CLIENT ?? process?.env?.CLIENT;
const useOpenSearch = import.meta.env?.VITE_USE_OPEN_SEARCH ?? process?.env?.USE_OPEN_SEARCH;
const disableRecordCreation = import.meta.env?.VITE_DISABLE_RECORD_CREATION;
const pbqaEvalEnabled = import.meta.env?.VITE_PBQA_EVAL_ENABLED ?? process?.env?.PBQA_EVAL_ENABLED;

const isPPL = client === 'ct-ppl';

const FEATURE_FLAGS = {
  Debugger: (env: Environment) => ['local', 'dev', 'staging', 'ephemeral'].includes(env),
  Endorsements: (env: Environment) => ['local'].includes(env) || tenantConfig.isEndorsementEnabled,
  Attachments: (env: Environment) => !isPPL && (['local'].includes(env) || tenantConfig.isAttachmentsEnabled),
  Branding: () => isPPL,
  // Disabled in ephemeral envs to stay within the malware-protection plan limit. When off, the
  // post-scan version bump that regenerates the contract PDF never fires (see useAttachmentMutations).
  MalwareScan: (env: Environment) => env !== 'ephemeral',
  SSO: () => tenantConfig.isSSOEnabled,
  Users: () => !isPPL,
  SaveClose: () => isPPL,
  PreDraftContract: () => isPPL,
  FirmOrderStatus: () => !isPPL,
  EndorsementReview: () => !isPPL,
  Comments: () => !isPPL,
  LibraryBlockActivityLog: () => !isPPL,
  ClauseDocxImport: () => !isPPL,
  TemplateDefaultContent: () => !isPPL,
  isPPLTestEnv: () => isPPL && pplCbTestEnvs.includes(env),
  /**
   * Contract Review Bot — eval configuration UI. Superadmin-only on the FE.
   * Intentionally gated to non-prod environments during the evaluation period;
   * the whole feature can be removed once the eval wraps up.
   */
  EvalConfigs: () => !isPPL,
  /**
   * Contract Guidance (PBQA) — the drawer, header badge, contracts-list column
   * and the `GET /feature_access` cohort lookup behind them. On only where the
   * env file says so: `VITE_PBQA_EVAL_ENABLED` mirrors terraform's
   * `enable_pbqa_eval`, which deploys the eval routes in a handful of PRO envs.
   * Everywhere else the routes do not exist, so the lookup would only 403.
   * Same lifecycle as EvalConfigs and removed once the eval wraps up.
   */
  PbqaEval: () => !isPPL && pbqaEvalEnabled === 'true',
  TemplateReviewWorkflow: () =>
    ['local', 'dev', 'staging', 'test', 'ephemeral'].includes(env) || tenantConfig.isTemplateReviewWorkflowEnabled,
  OpenSearch: () => useOpenSearch === 'true',
  DisableRecordCreation: () => disableRecordCreation === 'true',
};

export type FeatureFlag = keyof typeof FEATURE_FLAGS;

/** Central place for feature flags management. */
export const getFeatureFlag = (featureFlagName: FeatureFlag) => FEATURE_FLAGS[featureFlagName].call(null, env) ?? false;
